import { useEffect, useEffectEvent } from 'react';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type { SupportRealtimeEvent, SupportTicketStatusOption } from 'chaika-frontend-types/frontend-cms';

import { getSupportErrorCode, SupportApi } from '@api/SupportApi';
import { SupportRealtimeClient } from '@api/SupportRealtimeClient';

import { supportKeys } from '../queries/supportQueries';
import { SupportTicketListFilter, toTicketListQuery } from '../utils/ticketListFilter';
import { applySupportRealtimeEvent } from './supportRealtimeState';

interface Params {
    filter: SupportTicketListFilter;
    onTicketRemoved(ticketId: string): void;
    onAuthorizationRequired?(): void;
    selectedTicketId?: string;
}

const STATUS_DIRECTORY_REFETCH_INTERVAL_MS = 30_000;

export function useSupportRealtime({
    filter,
    onTicketRemoved,
    onAuthorizationRequired,
    selectedTicketId,
}: Params): void {
    const queryClient = useQueryClient();
    const getSelectedTicketId = useEffectEvent(() => selectedTicketId);
    const getFilter = useEffectEvent(() => filter);
    const handleTicketRemoved = useEffectEvent((ticketId: string) => onTicketRemoved(ticketId));
    const handleAuthorizationRequired = useEffectEvent(() => onAuthorizationRequired?.());

    useEffect(() => {
        let isDisposed = false;
        let generation = 0;
        let bufferedEvents: SupportRealtimeEvent[] | undefined;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;
        let statusDirectoryRefetchedAt = Number.NEGATIVE_INFINITY;

        const refetchStatusDirectoryForUnknownName = (statusName: string): void => {
            const statuses = queryClient.getQueryData<SupportTicketStatusOption[]>(supportKeys.ticketStatuses());
            const isKnown = !statuses || statuses.some(status => status.name === statusName);
            if (isKnown || Date.now() - statusDirectoryRefetchedAt < STATUS_DIRECTORY_REFETCH_INTERVAL_MS) return;
            statusDirectoryRefetchedAt = Date.now();
            void queryClient.invalidateQueries({ queryKey: supportKeys.ticketStatuses() });
        };

        const applyEvent = (event: SupportRealtimeEvent): { refreshList: boolean; reloadOpenTicket: boolean } => {
            const selectedTicketId = getSelectedTicketId();
            const previousStatus =
                event.type === 'ticket.status_changed' && event.payload.ticketId === selectedTicketId
                    ? queryClient.getQueryData<{ status: string }>(supportKeys.ticket(selectedTicketId))?.status
                    : undefined;
            const needsRefresh = applySupportRealtimeEvent(queryClient, event);
            if (event.type === 'ticket.status_changed') refetchStatusDirectoryForUnknownName(event.payload.statusName);
            if (event.type === 'ticket.removed') handleTicketRemoved(event.payload.ticketId);
            return {
                refreshList: needsRefresh,
                reloadOpenTicket:
                    event.type === 'ticket.status_changed' &&
                    event.payload.ticketId === selectedTicketId &&
                    previousStatus === 'deleted' &&
                    event.payload.status !== 'deleted',
            };
        };

        const synchronize = async (includeOpenTicket: boolean): Promise<void> => {
            const currentGeneration = ++generation;
            bufferedEvents ??= [];
            if (retryTimer) clearTimeout(retryTimer);
            const ticketId = includeOpenTicket ? getSelectedTicketId() : undefined;
            const filter = getFilter();
            const ticketListKey = supportKeys.ticketList(filter);
            const cancelSnapshotQueries = () =>
                Promise.all([
                    queryClient.cancelQueries({ queryKey: ticketListKey, exact: true }),
                    ...(ticketId ? [queryClient.cancelQueries({ queryKey: supportKeys.ticket(ticketId) })] : []),
                ]);
            try {
                await cancelSnapshotQueries();
                const tickets = await SupportApi.listTickets(toTicketListQuery(filter));
                const listedTicket = ticketId ? tickets.items.find(ticket => ticket.id === ticketId) : undefined;
                const detail = ticketId
                    ? await loadOpenTicket(ticketId, listedTicket?.status === 'deleted')
                    : undefined;
                if (isDisposed || generation !== currentGeneration) return;
                await cancelSnapshotQueries();
                if (isDisposed || generation !== currentGeneration) return;
                queryClient.removeQueries({
                    queryKey: supportKeys.ticketLists(),
                    predicate: query => query.queryHash !== hashKey(ticketListKey),
                });
                queryClient.setQueryData(ticketListKey, { pages: [tickets], pageParams: [undefined] });
                queryClient.setQueryData(supportKeys.totalUnread(), tickets.totalUnread);
                if (ticketId && detail) {
                    queryClient.setQueryData(supportKeys.ticket(ticketId), detail.ticket);
                    if (detail.messages) {
                        queryClient.setQueryData(supportKeys.messages(ticketId), {
                            pages: [detail.messages],
                            pageParams: [undefined],
                        });
                    }
                } else if (ticketId) {
                    applySupportRealtimeEvent(queryClient, { type: 'ticket.removed', payload: { ticketId } });
                    handleTicketRemoved(ticketId);
                }
                const events = bufferedEvents ?? [];
                let refreshList = false;
                let reloadOpenTicket = false;
                events.forEach(event => {
                    const application = applyEvent(event);
                    refreshList = application.refreshList || refreshList;
                    reloadOpenTicket = application.reloadOpenTicket || reloadOpenTicket;
                });
                bufferedEvents = undefined;
                if (reloadOpenTicket) void synchronize(true);
                else if (refreshList) void synchronize(false);
            } catch {
                if (isDisposed || generation !== currentGeneration) return;
                console.error('[SupportRealtime] REST resync failed; retrying');
                retryTimer = setTimeout(() => {
                    void synchronize(includeOpenTicket);
                }, 2_000);
            }
        };

        const client = new SupportRealtimeClient({
            onConnected: () => {
                void synchronize(true);
            },
            onDisconnected: () => {
                generation += 1;
                bufferedEvents = [];
                if (retryTimer) clearTimeout(retryTimer);
            },
            onAuthorizationRequired: () => handleAuthorizationRequired(),
            onEvent: event => {
                if (bufferedEvents) bufferedEvents.push(event);
                else {
                    const application = applyEvent(event);
                    if (application.reloadOpenTicket) void synchronize(true);
                    else if (application.refreshList) void synchronize(false);
                }
            },
        });
        client.connect();

        return () => {
            isDisposed = true;
            generation += 1;
            if (retryTimer) clearTimeout(retryTimer);
            client.close();
        };
    }, [queryClient]);
}

async function loadOpenTicket(ticketId: string, isListedDeleted: boolean) {
    try {
        const ticket = await SupportApi.getTicket(ticketId);
        if (isListedDeleted || ticket.status === 'deleted') {
            return { ticket: { ...ticket, status: 'deleted' as const } };
        }
        try {
            const messages = await SupportApi.listMessages(ticketId);
            return { ticket, messages };
        } catch (error) {
            if (getSupportErrorCode(error) === 'TICKET_DELETED') {
                return { ticket: { ...ticket, status: 'deleted' as const } };
            }
            throw error;
        }
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) return undefined;
        throw error;
    }
}
